import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  ApproveTimesheetOvertimeRequestDocument,
  DeleteTimesheetOvertimeRequestDocument,
  RejectTimesheetOvertimeRequestDocument,
  TimesheetOvertimeRequestDocument,
  UpdateTimesheetOvertimeRequestMutationVariables,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useFormContext } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'
import app from 'modules/core/store/models/app'

interface Props {
  canApprove: boolean
  isApprover: boolean
  isAuthor: boolean
  submitting: boolean
  handleClose: () => void
}

const ModalFooter = ({ canApprove, isApprover, isAuthor, submitting, handleClose }: Props) => {
  const form = useFormContext<UpdateTimesheetOvertimeRequestMutationVariables>()

  const onSuccess = () => {
    dispatch({ type: EventTypes.ATTENDANCE_SHIFT_TOGGLED })
    handleClose()
    app.setFlash({
      type: 'correct',
      message: i18n.t('common.saved_successfully'),
    })
  }

  const [approveOvertimeRequest, { loading: approving }] = useMutation(
    ApproveTimesheetOvertimeRequestDocument,
    {
      extractorFn: (data) => data.attendanceMutations.approveAttendanceOvertimeRequest,
      onCompleted: (data) => {
        if (data.attendanceMutations.approveAttendanceOvertimeRequest?.overtimeRequest) {
          onSuccess()
        }
      },
    }
  )

  const [rejectOvertimeRequest, { loading: rejecting }] = useMutation(
    RejectTimesheetOvertimeRequestDocument,
    {
      extractorFn: (data) => data.attendanceMutations.rejectAttendanceOvertimeRequest,
      onCompleted: (data) => {
        if (data.attendanceMutations.rejectAttendanceOvertimeRequest?.overtimeRequest) {
          onSuccess()
        }
      },
    }
  )

  const [deleteOvertimeRequest, { loading: deleting }] = useMutation(
    DeleteTimesheetOvertimeRequestDocument,
    {
      extractorFn: (data) => data.attendanceMutations.deleteAttendanceOvertimeRequest,
      refetchQueries: [TimesheetOvertimeRequestDocument],
      onCompleted: (data) => {
        if (data.attendanceMutations.deleteAttendanceOvertimeRequest?.overtimeRequest) {
          onSuccess()
        }
      },
    }
  )

  const loading = submitting || approving || deleting || rejecting

  return (
    <>
      {canApprove && isApprover && (
        <>
          <Button
            type="negative"
            disabled={loading}
            onClick={() => {
              const reason = form.values.reason
              if (!reason) {
                return form.setErrors({
                  reason: i18n.t('time_tracking.modals.overtime_request.reason_required'),
                })
              }
              rejectOvertimeRequest({
                variables: { id: form.values.id, reason: reason },
              })
            }}
          >
            {i18n.t('common.reject')}
          </Button>
          <Button
            type="positive"
            disabled={loading}
            onClick={() =>
              approveOvertimeRequest({
                variables: { id: form.values.id, reason: form.values.reason },
              })
            }
          >
            {i18n.t('common.approve')}
          </Button>
        </>
      )}
      {isAuthor && (
        <Button
          type="negative"
          disabled={loading}
          onClick={() =>
            deleteOvertimeRequest({
              variables: { id: form.values.id },
            })
          }
        >
          {i18n.t('common.delete')}
        </Button>
      )}
      <Button type="primary" disabled={loading} onClick={() => form.handleSubmit()}>
        {i18n.t('common.save_changes')}
      </Button>
    </>
  )
}

export default observer(ModalFooter)
